import { Chunk } from "../Chunk/Chunk";

/**
 *
 */
export class Chunker<T> {
    static Chunk = Chunk;
    // ========================================================================
    /**
     * Used for testing purposes
     */
    private static getInstanceMap<K>(intance: Chunker<K>): Map<symbol, Chunker.IChunk<K>> {
        return intance.#map;
    }

    // ========================================================================
    #map: Map<symbol, Chunker.IChunk<T>> = new Map();
    #options;

    // ========================================================================
    public constructor(options: Chunker.Options<T>) {
        this.#options = options;
    }

    // ========================================================================
    /**
     * Returns a Chunk from the map that has available capacity.
     * If no current Chunks have capacity, creates a new one and
     * adds it to the map.
     */
    #getChunkWithCapacity(): Chunker.IChunk<T> {
        const existingChunkWithCapacity = this.#chunks.find((chunk) => chunk.hasCapacity);
        if (existingChunkWithCapacity) {
            return existingChunkWithCapacity;
        }

        const newChunkWithCapacity = new Chunk(this.#options.chunkOptions);
        this.#map.set(newChunkWithCapacity.id, newChunkWithCapacity);
        return newChunkWithCapacity;
    }

    // ========================================================================
    #removeChunkFromMap(chunk: Chunker.IChunk<T>): void {
        this.#map.delete(chunk.id);
    }

    // ========================================================================
    get #chunks(): Chunker.IChunk<T>[] {
        return Array.from(this.#map.values());
    }

    // ========================================================================
    /**
     * Adds an item to an available Chunk
     */
    addToChunk(item: T): Promise<void> {
        const chunk = this.#getChunkWithCapacity();
        return chunk.add(item).finally(() => this.#removeChunkFromMap(chunk));
    }

    // ========================================================================
    /**
     * Save all pending entities
     */
    async saveChunks(): Promise<void> {
        await Promise.all(this.#chunks.map((chunk) => chunk.process())).finally(() => this.#map.clear());
    }
}

export namespace Chunker {
    export interface IChunkConstructor<T> {
        new (options: ChunkOptions<T>): IChunk<T>;
    }

    export interface Options<T> {
        chunkOptions: ChunkOptions<T>;
    }

    export interface IChunk<T> {
        readonly id: symbol;
        readonly hasCapacity: boolean;
        add(item: T): Promise<void>;
        process(): Promise<unknown>;
    }

    export interface ChunkOptions<T> {
        size: number;
        processFn(items: T[]): Promise<unknown>;
    }
}
